import * as React from 'react'

import {
  Button,
  Dialog,
  Flex,
  Form,
  Heading,
  Input,
  Select,
  Textarea,
  toast,
} from '@buffer-mono/popcorn'

import { EmojiPicker } from '~publish/components/EmojiPicker'
import { FormError } from '~publish/components/TemplateManager/FormError'
import { useForm } from '~publish/components/TemplateManager/useForm'

import {
  useAccount,
  useHasEntitlement,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'
import {
  CreatePostTemplateMutation,
  UpdatePostTemplateMutation,
} from '~publish/pages/Create/Templates/graphql/postTemplates'

import {
  CUSTOM_TEMPLATE_VISIBILITY,
  customTemplateFormDefaults,
  customTemplateFormSchema,
} from './schema'
import type {
  CustomTemplateFormValues,
  CustomTemplateVisibility,
} from './schema'

import styles from './CustomTemplateDialog.module.css'

import type { JSX } from 'react'

function isCustomTemplateVisibility(
  value: string,
): value is CustomTemplateVisibility {
  return CUSTOM_TEMPLATE_VISIBILITY.some((option) => option.value === value)
}

export type CustomTemplateDialogMode = 'create' | 'edit' | 'duplicate'

export type CustomTemplateDialogTemplate = {
  id: string
  title: string
  body: string
  description?: string
  emoji?: string
  visibility: CustomTemplateVisibility
}

export type CustomTemplateDialogProps = {
  mode: CustomTemplateDialogMode
  template?: CustomTemplateDialogTemplate
  initialValues?: Partial<CustomTemplateFormValues>
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: (values: CustomTemplateFormValues) => void
}

const MODE_HEADINGS: Record<CustomTemplateDialogMode, string> = {
  create: 'New Template',
  edit: 'Edit Template',
  duplicate: 'Duplicate Template',
}

const MODE_SUBMIT_LABELS: Record<CustomTemplateDialogMode, string> = {
  create: 'Create Template',
  edit: 'Save Changes',
  duplicate: 'Create Copy',
}

function deriveInitialValues({
  mode,
  template,
  initialValues,
}: Pick<
  CustomTemplateDialogProps,
  'mode' | 'template' | 'initialValues'
>): Partial<CustomTemplateFormValues> | null {
  if (mode === 'edit' && template) {
    return {
      title: template.title,
      body: template.body,
      description: template.description ?? '',
      emoji: template.emoji ?? customTemplateFormDefaults.emoji,
      visibility: template.visibility,
    }
  }

  if (mode === 'duplicate' && template) {
    return {
      title: `${template.title} (copy)`,
      body: template.body,
      description: template.description ?? '',
      emoji: template.emoji ?? customTemplateFormDefaults.emoji,
      visibility: 'private',
      ...initialValues,
    }
  }

  return initialValues ?? null
}

export const CustomTemplateDialog = ({
  mode,
  template,
  initialValues,
  open,
  onOpenChange,
  onSaved,
}: CustomTemplateDialogProps): JSX.Element => {
  const organizationId = useOrganizationId()
  const hasTeamFeatures = useHasEntitlement('teamsPanel')
  const { account } = useAccount()

  const derivedInitial = React.useMemo(
    () => deriveInitialValues({ mode, template, initialValues }),
    [mode, template, initialValues],
  )

  const { values, errors, setField, validate, reset } = useForm({
    schema: customTemplateFormSchema,
    defaults: customTemplateFormDefaults,
    initialValues: derivedInitial,
  })

  // Reset the form when the dialog closes so the next open starts clean.
  React.useEffect(() => {
    if (!open) reset()
  }, [open, reset])

  // Refetching `MyPostTemplates` keeps the Mine tab in sync without the
  // brittleness of splicing into a relay-style cached connection. Revisit
  // if this query becomes hot.
  const [createTemplate, { loading: creating }] = useTypedMutation(
    CreatePostTemplateMutation,
    (data) => data.createPostTemplate,
    {
      successTypename: 'CreatePostTemplateSuccess',
      refetchQueries: ['MyPostTemplates'],
    },
  )

  // Update mode does not need a cache helper — Apollo merges the returned
  // `PostTemplate` (keyed by id) into the normalized cache automatically and
  // any query holding a reference re-renders.
  const [updateTemplate, { loading: updating }] = useTypedMutation(
    UpdatePostTemplateMutation,
    (data) => data.updatePostTemplate,
    {
      successTypename: 'UpdatePostTemplateSuccess',
    },
  )

  const submitting = creating || updating

  // Team-shared visibility is a team-plan feature (`teamsPanel` covers Team
  // and Agency). Without it we only offer "Only me" — except when editing a
  // template that is already team-shared (e.g. after a downgrade), where the
  // option stays listed so the select doesn't render an empty trigger. Keyed
  // off the template, not the form value, so the option doesn't vanish
  // mid-edit and can't leak into create mode.
  const visibilityOptions = CUSTOM_TEMPLATE_VISIBILITY.filter(
    (option) =>
      option.value !== 'internal' ||
      hasTeamFeatures ||
      (mode === 'edit' && template?.visibility === 'internal'),
  )

  const handleSubmit = async (): Promise<void> => {
    const parsed = validate()
    if (!parsed) return

    if (mode !== 'edit' && !account?.hasVerifiedEmail) {
      toast.error('Please verify your email address to create templates.')
      return
    }

    const fields = {
      title: parsed.title,
      body: parsed.body,
      description: parsed.description,
      emoji: parsed.emoji,
      visibility: parsed.visibility,
    }

    if (mode === 'edit') {
      if (!template) {
        toast.error('Could not save template. Please try again.')
        return
      }
      const result = await updateTemplate({
        variables: { input: { id: template.id, ...fields } },
      })
      if (!result.success) {
        toast.error('Could not save template. Please try again.')
        return
      }
      toast.success('Template updated')
    } else {
      if (!organizationId) {
        toast.error('Could not save template. Please try again.')
        return
      }
      // `source` is tracking-only — read by the `Template Created`
      // analytics event in the backend use case to distinguish first-time
      // authoring from copy-as-starting-point.
      const source = mode === 'duplicate' ? 'duplicate' : 'scratch'
      const result = await createTemplate({
        variables: { input: { organizationId, source, ...fields } },
      })
      if (!result.success) {
        toast.error('Could not save template. Please try again.')
        return
      }
      toast.success('Template created')
    }

    onSaved?.(parsed)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className={styles.content}>
        <Form
          className={styles.form}
          onSubmit={() => {
            void handleSubmit()
          }}
        >
          <Dialog.Header>
            <Dialog.Title>
              <Heading size="md">{MODE_HEADINGS[mode]}</Heading>
            </Dialog.Title>
          </Dialog.Header>

          <Dialog.Body>
            <Flex direction="column" gap="lg">
              <Form.Field name="title" className={styles.field}>
                <Form.Label>Title</Form.Label>
                <div className={styles.titleRow}>
                  <EmojiPicker
                    onEmojiSelect={(emoji) => setField('emoji', emoji)}
                  >
                    <button
                      type="button"
                      className={styles.emojiPickerButton}
                      aria-label="Choose template emoji"
                    >
                      {values.emoji || '📝'}
                    </button>
                  </EmojiPicker>

                  <Form.Control>
                    <Input
                      className={styles.titleInput}
                      value={values.title}
                      onChange={(e) => setField('title', e.target.value)}
                      maxLength={200}
                      placeholder="e.g. Weekly tip"
                      // oxlint-disable-next-line jsx-a11y/no-autofocus -- The dialog opens with focus on its primary title field.
                      autoFocus
                    />
                  </Form.Control>
                </div>
                {errors.title ? <FormError message={errors.title} /> : null}
              </Form.Field>

              <Form.Field name="description" className={styles.field}>
                <Form.Label>Description</Form.Label>
                <Form.Control>
                  <Textarea
                    value={values.description}
                    onChange={(e) => setField('description', e.target.value)}
                    maxLength={500}
                    rows={2}
                    placeholder="A line for your future self about when to use this template."
                  />
                </Form.Control>
                {errors.description ? (
                  <FormError message={errors.description} />
                ) : null}
              </Form.Field>

              <Form.Field name="body" className={styles.field}>
                <Form.Label>Body</Form.Label>
                <Form.Control>
                  <Textarea
                    className={styles.bodyTextarea}
                    value={values.body}
                    onChange={(e) => setField('body', e.target.value)}
                    maxLength={5000}
                    rows={10}
                    resize="vertical"
                    placeholder="The content that will be inserted into the composer."
                  />
                </Form.Control>
                {errors.body ? <FormError message={errors.body} /> : null}
              </Form.Field>

              <Form.Field name="visibility" className={styles.field}>
                <Form.Label>Visibility</Form.Label>
                <Select
                  value={values.visibility}
                  onValueChange={(value: string): void => {
                    if (isCustomTemplateVisibility(value)) {
                      setField('visibility', value)
                    }
                  }}
                >
                  <Select.Trigger
                    className={styles.visibilitySelect}
                    size="medium"
                    aria-label="Template visibility"
                  />
                  <Select.Content>
                    {visibilityOptions.map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select>
              </Form.Field>
            </Flex>
          </Dialog.Body>

          <Dialog.Footer>
            <Dialog.Close>
              <Button variant="tertiary" type="button" disabled={submitting}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button variant="primary" type="submit" disabled={submitting}>
              {MODE_SUBMIT_LABELS[mode]}
            </Button>
          </Dialog.Footer>
        </Form>
        <Dialog.CloseButton />
      </Dialog.Content>
    </Dialog>
  )
}
